import type { ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { cn } from './cn';
import { Starfield } from './Starfield';

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  /** A lucide icon element; defaults to an inbox. */
  icon?: ReactNode;
  /** A button or link that gets the user unstuck ("Clear filters", "Create part"). */
  action?: ReactNode;
  /** `page` for whole-route states (404, 403), `inline` (default) inside a card or table. */
  size?: 'inline' | 'page';
  className?: string;
}

/** Nothing to show (or no access, or not found): says why and what to do next, over a faint starfield. */
export function EmptyState({ title, description, icon, action, size = 'inline', className }: EmptyStateProps) {
  return (
    <div className={cn('relative isolate flex flex-col items-center justify-center gap-1 overflow-hidden px-4 text-center', size === 'page' ? 'min-h-[50vh] py-16' : 'py-10', className)}>
      <Starfield className="-z-10" opacity={size === 'page' ? 0.45 : 0.35} />
      <span className={cn('mb-2 flex items-center justify-center rounded-full bg-muted text-ink-faint', size === 'page' ? 'size-14 [&_svg]:size-7' : 'size-10 [&_svg]:size-5')} aria-hidden>
        {icon ?? <Inbox />}
      </span>
      <p className={cn('m-0 font-display font-semibold text-heading', size === 'page' ? 'text-xl' : 'text-base')}>{title}</p>
      {description && <p className="m-0 max-w-md text-sm text-ink-muted">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
